import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar,
  DollarSign,
  Building2,
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Tag,
  User,
  ChevronRight,
  X,
  ExternalLink
} from 'lucide-react';
import { Contract, ContractAnalysisJSON } from '../../../../types/contract.types';
import { decodeHtmlEntities } from '../../../../utils/textUtils';

interface ContractSidebarProps {
  contract: Contract | null;
  analysis?: ContractAnalysisJSON | null;
  onEdit?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const ContractSidebar: React.FC<ContractSidebarProps> = ({
  contract,
  analysis,
  onEdit,
  collapsed = false,
  onToggleCollapse
}) => {
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);

  if (!contract) {
    return (
      <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number, currency?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      expired: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      terminated: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    };
    return statusStyles[status] || statusStyles.draft;
  };

  const getReviewStatusBadge = (status: string) => {
    const statusStyles: Record<string, { bg: string; icon: React.ReactNode }> = {
      pending_review: {
        bg: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        icon: <Clock className="w-3.5 h-3.5" />
      },
      under_review: {
        bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        icon: <AlertCircle className="w-3.5 h-3.5" />
      },
      finalized: {
        bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        icon: <CheckCircle className="w-3.5 h-3.5" />
      },
      not_received: {
        bg: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
        icon: <FileText className="w-3.5 h-3.5" />
      }
    };
    return statusStyles[status] || statusStyles.not_received;
  };

  const getRiskIndicator = () => {
    if (!analysis?.executive_summary?.overall_favorability_score) return null;

    const score = analysis.executive_summary.overall_favorability_score;
    let color = 'text-green-500 dark:text-green-400';
    let bgColor = 'bg-green-100 dark:bg-green-900/30';
    let label = 'Low Risk';

    if (score < 4) {
      color = 'text-red-500 dark:text-red-400';
      bgColor = 'bg-red-100 dark:bg-red-900/30';
      label = 'High Risk';
    } else if (score < 7) {
      color = 'text-yellow-500 dark:text-yellow-400';
      bgColor = 'bg-yellow-100 dark:bg-yellow-900/30';
      label = 'Medium Risk';
    }

    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${bgColor}`}>
        <AlertTriangle className={`w-4 h-4 ${color}`} />
        <span className={`text-sm font-medium ${color}`}>{label}</span>
        <span className={`text-sm ${color}`}>({score}/10)</span>
      </div>
    );
  };

  const reviewStatus = getReviewStatusBadge(contract.reviewStatus);

  if (collapsed) {
    return (
      <button
        onClick={onToggleCollapse}
        className="p-2 bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        title="Expand sidebar"
      >
        <ChevronRight className="w-5 h-5 text-gray-500" />
      </button>
    );
  }

  return (
    <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border overflow-hidden sticky top-24">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-border">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {decodeHtmlEntities(contract.contractTitle)}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {contract.supplier}
            </p>
          </div>
          {onEdit && (
            <button
              onClick={onEdit}
              className="ml-2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <FileText className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Status badges */}
        <div className="flex flex-wrap gap-2 mt-3">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(contract.status)}`}>
            {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
          </span>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${reviewStatus.bg}`}>
            {reviewStatus.icon}
            {contract.reviewStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </span>
        </div>
      </div>

      {/* Risk Indicator */}
      {analysis && (
        <div className="px-5 py-3 border-b border-gray-200 dark:border-dark-border">
          {getRiskIndicator()}
        </div>
      )}

      {/* Contract Details */}
      <div className="p-5 space-y-4">
        {/* Dates */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Contract Period
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Start</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatDate(contract.startDate)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">End</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatDate(contract.endDate)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Value */}
        {contract.value !== undefined && contract.value > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Contract Value
            </h3>
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-gray-400" />
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatCurrency(contract.value, contract.paymentCurrency)}
              </span>
            </div>
          </div>
        )}

        {/* Category */}
        {contract.category && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Category
            </h3>
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-900 dark:text-white">
                {contract.category}
              </span>
            </div>
          </div>
        )}

        {/* Department */}
        {contract.department && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Department
            </h3>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-900 dark:text-white">
                {contract.department}
              </span>
            </div>
          </div>
        )}

        {/* Created By */}
        {contract.createdBy && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Created By
            </h3>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-900 dark:text-white">
                {typeof contract.createdBy === 'object' && contract.createdBy !== null
                  ? `${(contract.createdBy as any).firstName || ''} ${(contract.createdBy as any).lastName || ''}`.trim() || (contract.createdBy as any).email || 'Unknown'
                  : contract.createdBy || 'Unknown'}
              </span>
            </div>
          </div>
        )}

        {/* Payment Terms */}
        {contract.paymentTerms && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Payment Terms
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {contract.paymentTerms}
            </p>
          </div>
        )}

        {/* Analysis Summary */}
        {analysis?.executive_summary?.overview && (
          <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                AI Analysis Summary
              </h3>
              <button
                onClick={() => setShowAnalysisModal(true)}
                className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                View Details
              </button>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-4">
              {analysis.executive_summary.overview}
            </p>
            {analysis.executive_summary.critical_issues && analysis.executive_summary.critical_issues.length > 0 && (
              <button
                onClick={() => setShowAnalysisModal(true)}
                className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors mt-2"
              >
                <AlertTriangle className="w-3 h-3" />
                {analysis.executive_summary.critical_issues.length} critical issue(s) - Click to view
              </button>
            )}
          </div>
        )}

        {/* Analysis Modal - Using Portal to render outside component hierarchy */}
        {showAnalysisModal && analysis?.executive_summary && createPortal(
          <div className="fixed inset-0 z-[9999] overflow-y-auto">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
              onClick={() => setShowAnalysisModal(false)}
            />

            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <div className="relative w-full max-w-2xl bg-white dark:bg-dark-surface rounded-2xl shadow-2xl border border-gray-200 dark:border-dark-border overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800/50">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      AI Analysis Summary
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {decodeHtmlEntities(contract?.contractTitle)}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAnalysisModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
                  {/* Key Stats Grid */}
                  <div className="mb-6 grid grid-cols-3 gap-3">
                    {/* Favorability Score */}
                    <div className={`p-3 rounded-xl text-center ${
                      (analysis.executive_summary.overall_favorability_score || 0) <= 3
                        ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                        : (analysis.executive_summary.overall_favorability_score || 0) <= 6
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                        : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    }`}>
                      <div className={`text-2xl font-bold ${
                        (analysis.executive_summary.overall_favorability_score || 0) <= 3
                          ? 'text-red-600 dark:text-red-400'
                          : (analysis.executive_summary.overall_favorability_score || 0) <= 6
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {analysis.executive_summary.overall_favorability_score || 'N/A'}/10
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Favorability</div>
                    </div>

                    {/* Total Clauses */}
                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {analysis.clauses?.length || 0}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Clauses Analyzed</div>
                    </div>

                    {/* Critical Issues */}
                    <div className={`p-3 rounded-xl text-center ${
                      (analysis.executive_summary.critical_issues?.length || 0) > 0
                        ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                        : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    }`}>
                      <div className={`text-2xl font-bold ${
                        (analysis.executive_summary.critical_issues?.length || 0) > 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {analysis.executive_summary.critical_issues?.length || 0}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Critical Issues</div>
                    </div>
                  </div>

                  {/* Overview - Split into readable paragraphs */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                      Overview
                    </h3>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                      {(() => {
                        const text = analysis.executive_summary.overview || '';
                        // Split by sentences and group into logical paragraphs (2-3 sentences each)
                        const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
                        const paragraphs: string[] = [];
                        let currentPara = '';

                        sentences.forEach((sentence, idx) => {
                          currentPara += sentence;
                          // Create a new paragraph every 2-3 sentences or at natural breaks
                          if ((idx + 1) % 3 === 0 || idx === sentences.length - 1) {
                            paragraphs.push(currentPara.trim());
                            currentPara = '';
                          }
                        });

                        return paragraphs.map((para, idx) => (
                          <p key={idx} className="text-sm">
                            {para}
                          </p>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Critical Issues */}
                  {analysis.executive_summary.critical_issues && analysis.executive_summary.critical_issues.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        Critical Issues ({analysis.executive_summary.critical_issues.length})
                      </h3>
                      <div className="space-y-3">
                        {analysis.executive_summary.critical_issues.map((issue, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-xl ${
                              issue.risk_level === 'High'
                                ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                                : issue.risk_level === 'Medium'
                                ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                                : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`flex-shrink-0 p-2 rounded-lg ${
                                issue.risk_level === 'High'
                                  ? 'bg-red-100 dark:bg-red-900/40'
                                  : issue.risk_level === 'Medium'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/40'
                                  : 'bg-gray-100 dark:bg-gray-700'
                              }`}>
                                <AlertTriangle className={`w-5 h-5 ${
                                  issue.risk_level === 'High'
                                    ? 'text-red-600 dark:text-red-400'
                                    : issue.risk_level === 'Medium'
                                    ? 'text-yellow-600 dark:text-yellow-400'
                                    : 'text-gray-600 dark:text-gray-400'
                                }`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold text-gray-900 dark:text-white">
                                    {issue.title}
                                  </h4>
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                    issue.risk_level === 'High'
                                      ? 'bg-red-200 text-red-800 dark:bg-red-900/60 dark:text-red-300'
                                      : issue.risk_level === 'Medium'
                                      ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-900/60 dark:text-yellow-300'
                                      : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                  }`}>
                                    {issue.risk_level}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  {issue.description}
                                </p>
                                {issue.recommended_action && (
                                  <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                    <CheckCircle className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-primary-700 dark:text-primary-400">
                                      <span className="font-medium">Recommended Action:</span> {issue.recommended_action}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800/50">
                  <button
                    onClick={() => setShowAnalysisModal(false)}
                    className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};
